import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Program from 'wwg-utilities/dist/collections/program';
import Provider from 'wwg-utilities/dist/collections/provider';
import getProgramsByOnetCodeRequest from '../../../lib/api/getProgramsByOnetCodeRequest';
import getProgramSlug from '../../../lib/data/getProgramSlug';
import ArrowLeft from '../../icons/ArrowLeft';
import ExternalLink from '../../icons/ExternalLink';
import dynamic from 'next/dynamic';
const BrowserOnly = dynamic(() => import('../../shared/BrowserOnly'), {
  ssr: false,
})

interface EducationPathwaysProps {
  career: any;
}

const EducationPathways: React.FC<EducationPathwaysProps> = ({ career }) => {
  const pathwaysNextRef = useRef<HTMLDivElement>(null);
  const pathwaysPrevRef = useRef<HTMLDivElement>(null);
  const [swiper, setSwiper] = useState<any | null>(null);
  const getFormattedSchoolUrl = (url: string): string => {
    if (url.substring(0, 8) != 'https://') {
      return `https://${url}`;
    }

    return url;
  };

  const { data: programs } = useQuery([JSON.stringify(career.id)], () => {
    return getProgramsByOnetCodeRequest(career.onetCode);
  });
  if (!programs?.length) {
    return <></>;
  }
  return (
    <>
      <section className="my-18">
        <h2 className="text-2xl font-semibold md:text-4xl leading-heading2 text-careerPageHeading">
          Education Pathways
        </h2>
        <p className="my-2 text-base leading-6 md:my-8 md:text-lg">
          Check out local colleges and training programs that will get you
          started on the path to this career.{' '}
        </p>
        <BrowserOnly>
          <section className="flex items-center my-6 navigation md:my-0">
            <figure
              onClick={() => {
                if (!swiper) return;
                swiper.slidePrev();
              }}
              className="p-5 mr-2 border-2 border-solid rounded-md cursor-pointer prev bg-careerPageHighlight border-careerPageHighlight hover:bg-white stroke-white hover:stroke-careerPageHighlight"
            >
              <ArrowLeft className="h-7 w-7 stroke-inherit" />
            </figure>
            <figure
              onClick={() => {
                if (!swiper) return;
                swiper.slideNext();
              }}
              ref={pathwaysNextRef}
              className="p-5 ml-2 border-2 border-solid rounded-md cursor-pointer next bg-careerPageHighlight border-careerPageHighlight hover:bg-white stroke-white hover:stroke-careerPageHighlight"
            >
              <ArrowLeft className="rotate-180 h-7 w-7 stroke-inherit" />
            </figure>
          </section>
          <Swiper
            direction="horizontal"
            spaceBetween={30}
            modules={[Navigation]}
            navigation={{
              nextEl: pathwaysNextRef?.current,
              prevEl: pathwaysPrevRef?.current,
            }}
            onSwiper={(swiper: any) => setSwiper(swiper)}
            loop={true}
            className="w-full my-10"
            allowTouchMove={false}
            draggable={false}
            slideToClickedSlide={false}
            shortSwipes={false}
            preventClicks={true}
            longSwipes={false}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
            }}
          >
            {programs?.map((program: Program) => {
              return (
                <SwiperSlide key={program.id}>
                  <div className="p-8 rounded-2xl bg-white grid grid-rows-3 items-center h-[234px]">
                    <h3 className="text-xl font-semibold break-words md:text-2xl line-clamp-3">
                      {program.name}
                    </h3>
                    <p className="md:text-lg text-gray4">
                      at {(program.institution as Provider).name}
                    </p>
                    <Link
                      className="flex items-center font-semibold underline underline-offset-2 md:text-lg text-careerPageHeading hover:text-gray-8 hover:fill-gray8 fill-careerPageHeading"
                      target="_blank"
                      href={`/programs${getProgramSlug(program)}`}
                    >
                      Learn more
                      <ExternalLink className="w-4 h-4 ml-2 stroke-1 fill-inherit" />
                    </Link>
                  </div>
                </SwiperSlide>
              );
            })}
            {career?.training?.map((training: any) => {
              return (
                <SwiperSlide key={training.id}>
                  <div className="p-8 rounded-2xl bg-white grid grid-rows-3 items-center h-[234px]">
                    <h3 className="text-xl font-semibold break-words md:text-2xl line-clamp-3">
                      {training.programName}
                    </h3>
                    <p className="md:text-lg text-gray4">
                      at {training.schoolName}
                    </p>
                    <Link
                      target="_blank"
                      className="flex items-center font-semibold underline underline-offset-2 md:text-lg text-careerPageHeading hover:text-gray8 fill-careerPageHeading hover:fill-gray8"
                      href={getFormattedSchoolUrl(training.schoolUrl)}
                    >
                      Learn more
                      <ExternalLink className="w-4 h-4 ml-2 stroke-1 fill-inherit" />
                    </Link>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </BrowserOnly>
      </section>
    </>
  );
};

export default EducationPathways;
